<template>
  <div class="modal-content">
    <div class="modal-body">
      <div class="row">
        <div class="col-md-12">
          <h2>{{ _('Payment terminal') }} ({{ terminal }})</h2>
          <h3 v-if="status">{{ statusText }}</h3>
          <h3 v-else>{{ _('Loading...') }}</h3>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-warning" v-on:click="abort" :disabled="!canAbort"><i class="fa fa-fw fa-ban"></i>{{ _('Abort') }}</button>
      <button type="button" class="btn btn-info" v-on:click="restartPolling" :disabled="!canRestartPolling"><i class="fa fa-fw fa-refresh"></i>{{ _('Check status') }}</button>
      <button type="button" class="btn btn-default hidden-xs" v-on:click="close" :disabled="!canClose"><i class="fa fa-close fa-fw"></i>{{ _('Close') }}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import events from '~/lib/events'

const POLL_INTERVAL_MS = 1000
const WATCHDOG_INTERVAL_MS = 3000

export default {
  mixins: [events],
  props: ['shopId', 'amount', 'terminal', 'session', 'pollUrl', 'callbackUrl'],
  data () {
    return {
      status: '',
      statusText: '',
      response: null,
      pollTimer: null,
      watchdogTimer: null,
      pollAbortController: null,
      pollInFlight: false,
      pollGeneration: 0,
      pollingStopped: false,
      onVisibilityChange: null,
      onPageResume: null
    }
  },
  computed: {
    canAbort () {
      return this.status !== 'SUCCESS'
    },
    canClose () {
      return this.status !== 'SUCCESS'
    },
    canRestartPolling () {
      return !this.pollingStopped && this.status !== 'SUCCESS' && this.status !== 'ERROR'
    },
  },
  methods: {
    isTerminalStatus () {
      return this.status === 'SUCCESS' || this.status === 'ERROR'
    },
    shouldContinuePolling () {
      return !this.pollingStopped && !this.isTerminalStatus()
    },
    stopPolling () {
      this.pollingStopped = true
      this.pollGeneration++
      this.clearPollTimer()
      this.clearWatchdog()
      this.abortInflightPoll()
      this.pollInFlight = false
    },
    clearPollTimer () {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    clearWatchdog () {
      if (this.watchdogTimer) {
        clearInterval(this.watchdogTimer)
        this.watchdogTimer = null
      }
    },
    startWatchdog () {
      this.clearWatchdog()
      this.watchdogTimer = setInterval(() => {
        if (!this.shouldContinuePolling() || this.pollInFlight) return
        this.pollOnce()
      }, WATCHDOG_INTERVAL_MS)
    },
    abortInflightPoll () {
      if (this.pollAbortController) {
        this.pollAbortController.abort()
        this.pollAbortController = null
      }
    },
    // SoftPOS keeps the WebView hidden for the whole card interaction — never gate
    // scheduling on document.hidden, or the poll loop dies when Viva comes to foreground.
    schedulePoll (delay = POLL_INTERVAL_MS) {
      if (!this.shouldContinuePolling() || this.pollTimer) return
      this.pollTimer = setTimeout(() => this.pollOnce(), delay)
    },
    // Force an immediate poll after SoftPOS returns (visibility/focus/pageshow).
    forceResumePolling () {
      if (!this.shouldContinuePolling()) return
      this.clearPollTimer()
      if (this.pollInFlight) return
      this.pollOnce()
    },
    restartPolling () {
      if (!this.canRestartPolling) return
      this.clearPollTimer()
      this.abortInflightPoll()
      this.pollInFlight = false
      this.pollOnce()
    },
    isPollAborted (error) {
      return axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED'
    },
    async pollOnce () {
      if (!this.shouldContinuePolling() || this.pollInFlight) return

      this.clearPollTimer()
      const generation = ++this.pollGeneration
      this.pollInFlight = true
      this.abortInflightPoll()
      this.pollAbortController = new AbortController()

      try {
        const data = {
          method: 'getSessionDetails',
          sessionId: this.session
        }
        const response = await axios.post(this.pollUrl, data, {
          signal: this.pollAbortController.signal
        })
        if (this.pollingStopped || generation !== this.pollGeneration) return
        this.response = response.data
        this.processSessionDetails(response.data)
      } catch (e) {
        // Aborts and network errors must not leave the loop dead — fall through to reschedule.
        if (!this.isPollAborted(e)) {
          console.log('viva poll error', e)
        }
      } finally {
        if (generation === this.pollGeneration) {
          this.pollInFlight = false
          this.pollAbortController = null
        }
      }

      if (generation === this.pollGeneration && this.shouldContinuePolling()) {
        this.schedulePoll()
      }
    },
    handleVisibilityChange () {
      // Hide must not tear down the poll loop (SoftPOS). Visible = resume accelerator only.
      if (!document.hidden) {
        this.forceResumePolling()
      }
    },
    handlePageResume () {
      if (document.hidden) return
      this.forceResumePolling()
    },
    success () {
      this.stopPolling()
      this.$emit('success', this.response)
    },
    close () {
      if (this.status === 'SUCCESS') {
        return
      }
      this.stopPolling()
      this.$emit('close', {
        errorMessage: this.status === 'ERROR' ? this.statusText : this._('Payment cancelled.')
      })
    },
    abort () {
      this.stopPolling()
      this.$emit('abort', {
        errorMessage: this.status === 'ERROR' ? this.statusText : this._('Payment aborted.')
      })
    },
    isAndroidBrowser() {
      const userAgent = navigator.userAgent.toLowerCase()
      return /android/.test(userAgent)
    },
    processSessionDetails(data) {
      if (data.detail) {
        this.status = 'LOADING'
        this.statusText = data.detail
        return
      }
      if (data.success) {
        this.status = 'SUCCESS'
        this.statusText = data.message
        this.success()
      } else if (data.eventId > 0) {
        this.status = 'ERROR'
        this.statusText = data.message
        this.close()
      } else if (data.message) {
        this.status = 'PROCESSING'
        this.statusText = data.message
      }
    }
  },
  mounted () {
    if (this.isAndroidBrowser()) {
      window.open('vivapayclient://pay/v1?appId=com.johku.pos&merchantKey=deprecated&action=foreground&callback=' + this.callbackUrl)
    }
    this.onVisibilityChange = () => this.handleVisibilityChange()
    this.onPageResume = () => this.handlePageResume()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    window.addEventListener('focus', this.onPageResume)
    window.addEventListener('pageshow', this.onPageResume)
    this.startWatchdog()
    this.pollOnce()
  },
  beforeUnmount () {
    this.stopPolling()
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('focus', this.onPageResume)
    window.removeEventListener('pageshow', this.onPageResume)
  }
}
</script>
